/**
 * Browser Notifications Utility
 *
 * Provides functions for showing native browser notifications.
 * Used to alert users when background course generation completes.
 *
 * @author Tutara Team
 * @since January 2025
 */

// =============================================================================
// Permission Management
// =============================================================================

/**
 * Check if browser notifications are supported.
 */
export function isNotificationSupported(): boolean {
  return 'Notification' in window;
}

/**
 * Check current notification permission status.
 */
export function getNotificationPermission(): NotificationPermission | 'unsupported' {
  if (!isNotificationSupported()) {
    return 'unsupported';
  }
  return Notification.permission;
}

/**
 * Request notification permission from the user.
 * Returns true if permission is granted.
 */
export async function requestNotificationPermission(): Promise<boolean> {
  if (!isNotificationSupported()) {
    console.warn('[Notifications] Browser does not support notifications');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission === 'denied') {
    console.warn('[Notifications] Permission was previously denied');
    return false;
  }

  try {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  } catch (error) {
    console.error('[Notifications] Failed to request permission:', error);
    return false;
  }
}

// =============================================================================
// Notification Functions
// =============================================================================

interface NotificationOptions {
  body?: string;
  icon?: string;
  badge?: string;
  tag?: string;
  requireInteraction?: boolean;
  onClick?: () => void;
}

/**
 * Show a native browser notification.
 */
export function showNotification(
  title: string,
  options?: NotificationOptions
): Notification | null {
  if (!isNotificationSupported()) {
    console.warn('[Notifications] Browser does not support notifications');
    return null;
  }

  if (Notification.permission !== 'granted') {
    console.warn('[Notifications] Permission not granted');
    return null;
  }

  try {
    const notification = new Notification(title, {
      icon: options?.icon || '/logos/icon.png',
      badge: options?.badge || '/logos/icon.png',
      body: options?.body,
      tag: options?.tag,
      requireInteraction: options?.requireInteraction || false,
    });

    // Handle click
    notification.onclick = () => {
      window.focus();
      notification.close();
      options?.onClick?.();
    };

    // Auto-close after 10 seconds if not interacted with
    setTimeout(() => {
      notification.close();
    }, 10000);

    return notification;
  } catch (error) {
    console.error('[Notifications] Failed to show notification:', error);
    return null;
  }
}

// =============================================================================
// Course Generation Specific Notifications
// =============================================================================

/**
 * Show notification when course generation completes successfully.
 */
export function showCourseReadyNotification(
  courseTopic: string,
  onClick?: () => void
): Notification | null {
  return showNotification('Course Ready!', {
    body: `Your course "${courseTopic}" has been generated and is ready to start.`,
    tag: 'course-generation-complete',
    requireInteraction: true,
    onClick,
  });
}

/**
 * Show notification when course generation fails.
 */
export function showCourseFailedNotification(
  courseTopic: string,
  errorMessage?: string,
  onClick?: () => void
): Notification | null {
  return showNotification('Course Generation Failed', {
    body: errorMessage || `Failed to generate course "${courseTopic}". Please try again.`,
    tag: 'course-generation-failed',
    onClick,
  });
}

// =============================================================================
// Permission Prompt Helper
// =============================================================================

/**
 * Shows a notification permission prompt if not already granted or denied.
 * Returns true if user should be prompted (permission is 'default').
 */
export function shouldPromptForPermission(): boolean {
  if (!isNotificationSupported()) {
    return false;
  }
  return Notification.permission === 'default';
}
